import React, {Fragment, useCallback, useEffect, useRef, useState} from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import {CardHeader, Paper} from "@mui/material";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import {useTranslation} from "react-i18next";
import {connect} from "react-redux";
import Call from "../../../hocs/call";
import AlertDialog from "../../alert-dialog";
import ChartSettingsColors from "../../chart-settings-forms/Colors";
import ChartSettingsGeneral from "../../chart-settings-forms/General";
import ChartZoomIn from "../../chart-zoom/ChartZoomIn";
import ChartZoomOut from "../../chart-zoom/ChartZoomOut";
import ChartZoomReset from "../../chart-zoom/ChartZoomReset";
import {CHART_DIM_ORDER_SELECTOR_VALUE_DEFAULT, CHART_TYPE_BAR, CHART_TYPE_LINE} from "../../chart/constants";
import CustomDialog from "../../custom-dialog";
import CustomDialogTitle from "../../custom-dialog-title";
import CustomEmpty from "../../custom-empty";
import CsvIcon from "../../custom-icons/CsvIcon";
import DatasetFiltersJsonStatWrapper from "../../dataset-filters/DatasetFiltersJsonStatWrapper";
import {FONT_SIZE_SELECTOR_FONT_SIZE_MD, FONT_SIZE_SELECTOR_FONT_SIZE_SM} from "../../font-size-selector";
import MapGeneralSettings from "../../map-settings-form/General";
import MapSettingsSettings from "../../map-settings-form/Settings";
import TableLayoutLight from "../../table-layout-light";
import {
  TABLE_SETTINGS_CELLS_HEADER_MERGE_SIDE_MERGE,
  TABLE_SETTINGS_CELLS_HEADER_MERGE_SIDE_SPLIT,
  TABLE_SETTINGS_CELLS_HEADER_SPLIT_SIDE_MERGE,
  TABLE_SETTINGS_CELLS_HEADER_SPLIT_SIDE_SPLIT
} from "../../table-settings-form/constants";
import TableSettings from "../../table-settings-form/Settings";
import {TEMPORAL_DIM_ORDER_SELECTOR_VALUE_DESC} from "../../temporal-dim-order-selector/constants";
import VariationSelector from "../../variation-selector";
import ChartJsonStatWrapper from "../../viewer-stat-chart/ChartJsonStatWrapper";
import Map from "../../viewer-stat-map/single-viewer/Map";
import Table, {
  JSONSTAT_TABLE_FONT_SIZE_LG,
  JSONSTAT_TABLE_FONT_SIZE_MD,
  JSONSTAT_TABLE_FONT_SIZE_SM
} from "../../viewer-stat-table/single-viewer";
import {getViewerTypeFromIdx, isChartLayoutCartesianByViewerIdx, viewersFactory} from "../constant";
import {
  resetSVChartSorting,
  setDatasetSVChartSettings,
  setDatasetSVHtmlGeneratingTime,
  setDatasetSVLabelFormat,
  setDatasetSVMapDetailLevel,
  setDatasetSVMapSettings,
  setDatasetSVTableSettings,
  setDatasetSVTemporalDimOrder,
  setDatasetSVVariation,
  showDatasetSVLayout,
  submitDatasetSVChartLayout,
  submitDatasetSVDownload,
  submitDatasetSVMapLayout,
  submitDatasetSVTableFilterTree,
  submitDatasetSVTableLayout
} from "../../../state/dataset/single-viewer/actions";
import {getFilteredCriteriaFromLayout} from "../../../utils/criteria";
import {
  getDatasetSize,
  getDimensionLabel,
  getDimensionValueLabel,
  getFilterTreeFromJsonStat,
  getFormattedDimensionValueLabel,
  getInitialFiltersValue,
  getUpdatedLayout,
  MAX_ALLOWED_CELLS
} from "../../../utils/dataset";
import {DOWNLOAD_FORMAT_CSV, downloadFormats} from "../../../utils/download";
import {numberFormatter} from "../../../utils/formatters";

const $ = window.jQuery;

const actionStyle = {
  height: "40px",
  "& button": {
    padding: "8px"
  }
};

const paperStyle = {
  padding: "16px"
};

const cardHeaderStyle = {
  padding: "0 0 8px 0"
};

const mapStateToProps = ({app, appConfig, hub, dataset}) => {
  const viewerIdx = dataset.singleViewer.viewerIdx;

  const chartLayoutCartesian = dataset.singleViewer.chartLayoutCartesian;
  const chartLayoutRadial = dataset.singleViewer.chartLayoutRadial;
  const chartFilterTreeCartesian = dataset.singleViewer.chartFilterTreeCartesian;
  const chartFilterTreeRadial = dataset.singleViewer.chartFilterTreeRadial;

  return {
    defaultLanguage: app.language,
    languages: app.languages,
    maxObservations: hub.hub.maxObservationsAfterCriteria,
    maxCells: hub.hub.maxCells || MAX_ALLOWED_CELLS,
    showSingleGeometry: appConfig.mapConfig.showSingleGeometry,
    jsonStat: dataset.singleViewer.dataset,
    datasetFetchStart: dataset.singleViewer.datasetFetchStart,
    datasetFetchError: dataset.singleViewer.datasetFetchError,
    isPartialData: dataset.singleViewer.isPartialData,
    isEmptyData: dataset.singleViewer.isEmptyData,
    isTooBigData: dataset.singleViewer.isTooBigData,
    dimensions: dataset.singleViewer.dimensions,
    dimensionsInfo: dataset.singleViewer.dimensionsInfo,
    timeDim: dataset.singleViewer.timeDim,
    viewerIdx: dataset.singleViewer.viewerIdx,
    tableLayout: dataset.singleViewer.tableLayout,
    mapLayout: dataset.singleViewer.mapLayout,
    chartLayout: isChartLayoutCartesianByViewerIdx(viewerIdx) ? chartLayoutCartesian : chartLayoutRadial,
    chartLayoutCartesian: chartLayoutCartesian,
    chartLayoutRadial: chartLayoutRadial,
    tableFilterTree: dataset.singleViewer.tableFilterTree,
    mapFilterTree: dataset.singleViewer.mapFilterTree,
    chartFilterTree: isChartLayoutCartesianByViewerIdx(viewerIdx) ? chartFilterTreeCartesian : chartFilterTreeRadial,
    timePeriodsByFreq: dataset.singleViewer.timePeriodsByFreq,
    labelFormat: dataset.singleViewer.labelFormat,
    customLabelFormats: dataset.singleViewer.customLabelFormats,
    temporalDimOrder: dataset.singleViewer.temporalDimOrder,
    showTrend: dataset.singleViewer.showTrend,
    showCyclical: dataset.singleViewer.showCyclical,
    criteria: dataset.singleViewer.criteria,
    decimalSeparator: dataset.singleViewer.decimalSeparator,
    roundingStrategy: dataset.singleViewer.roundingStrategy,
    decimalPlaces: dataset.singleViewer.decimalPlaces,
    tableEmptyChar: dataset.singleViewer.tableEmptyChar,
    chartSettings: dataset.singleViewer.chartSettings,
    mapSettings: dataset.singleViewer.mapSettings,
    enableVariation: dataset.singleViewer.enableVariation,
    detailLevel: dataset.singleViewer.detailLevel,
    splitHeaderCells: dataset.singleViewer.splitHeaderCells,
    splitSideCells: dataset.singleViewer.splitSideCells,
    lastNObservations: dataset.singleViewer.lastNObservations,
    attributesAsDim: dataset.singleViewer.attributesAsDim,
    isPointData: dataset.singleViewer.isPointData,
    latAttributeId: dataset.singleViewer.latAttributeId,
    longAttributeId: dataset.singleViewer.longAttributeId,
    srid: dataset.singleViewer.srid
  };
};

const mapDispatchToProps = dispatch => ({
  onLayoutShow: () => dispatch(showDatasetSVLayout()),
  onTableLayoutSet: layout => dispatch(submitDatasetSVTableLayout(layout)),
  onTableFilterTreeSet: filterTree => dispatch(submitDatasetSVTableFilterTree(filterTree)),
  onMapLayoutSet: layout => dispatch(submitDatasetSVMapLayout(layout)),
  onChartLayoutSet: layout => dispatch(submitDatasetSVChartLayout(layout)),
  onChartSortingReset: isChartCartesian => dispatch(resetSVChartSorting(isChartCartesian)),
  onLabelFormatSet: labelFormat => dispatch(setDatasetSVLabelFormat(labelFormat)),
  onVariationSet: variation => dispatch(setDatasetSVVariation(variation)),
  onTableSettingsSet: tableSetting => dispatch(setDatasetSVTableSettings(tableSetting)),
  onChartSettingsSet: chartSetting => dispatch(setDatasetSVChartSettings(chartSetting)),
  onMapSettingsSet: mapSettings => dispatch(setDatasetSVMapSettings(mapSettings)),
  onDetailLevelSet: detailLevel => dispatch(setDatasetSVMapDetailLevel(detailLevel)),
  onTimeSet: time => dispatch(setDatasetSVHtmlGeneratingTime(time)),
  onDownloadSubmit: (
    nodeId,
    packageId,
    datasetId,
    datasetTitle,
    criteria,
    lastNObservations,
    layout,
    format,
    extension,
    zipped,
    params,
    defaultLanguage,
    languages,
    t
  ) =>
    dispatch(
      submitDatasetSVDownload(
        nodeId,
        packageId,
        datasetId,
        datasetTitle,
        criteria,
        lastNObservations,
        layout,
        format,
        extension,
        zipped,
        params,
        defaultLanguage,
        languages,
        t
      )
    ),
  onTemporalDimOrderSet: temporalDimOrder => dispatch(setDatasetSVTemporalDimOrder(temporalDimOrder))
});

const handleStyle = mapId => {
  const $actions = $("#data-viewer__viewer__header__actions");
  const $filters = $("#data-viewer__viewer__header__filters");
  const $filtersAndActions = $("#data-viewer__viewer__header__filters-actions-container");

  const actionsWidth = $actions.outerWidth(true) || 0;
  const filtersWidth = $filters.outerWidth(true) || 0;
  const filtersAndActionsWidth = $filtersAndActions.width() || 0;

  if (Math.ceil(filtersAndActionsWidth) > Math.ceil(actionsWidth + filtersWidth + 8)) {
    $filtersAndActions.css({flexDirection: "row"});
    $actions.css({alignSelf: "unset"});
  } else {
    $filtersAndActions.css({flexDirection: "column-reverse"});
    $actions.css({alignSelf: "end"});
  }

  const viewerHeaderHeight = $("#data-viewer__viewer__header").outerHeight(true) || 0;
  $("#data-viewer__viewer__viewers").height(`calc(100% - ${viewerHeaderHeight}px)`);

  if (window.LMap && mapId) {
    window.LMap.handleViewportChange(mapId);
  }
};

function SingleViewerViewer(props) {
  const {
    nodeId,
    nodeExtras,
    packageId,
    datasetId,
    datasetTitle,
    chartId,
    mapId,

    defaultLanguage,
    languages,
    maxObservations,
    maxCells,
    showSingleGeometry,
    jsonStat,
    datasetFetchStart,
    datasetFetchError,
    isPartialData,
    isEmptyData,
    isTooBigData,
    dimensions,
    dimensionsInfo,
    timeDim,
    viewerIdx,
    tableLayout,
    mapLayout,
    chartLayout,
    tableFilterTree,
    mapFilterTree,
    chartFilterTree,
    timePeriodsByFreq,
    labelFormat,
    customLabelFormats,
    temporalDimOrder,
    showTrend,
    showCyclical,
    criteria,
    decimalSeparator,
    roundingStrategy,
    decimalPlaces,
    tableEmptyChar,
    chartSettings,
    mapSettings,
    enableVariation,
    detailLevel,
    isFullscreen,
    splitHeaderCells,
    splitSideCells,
    lastNObservations,
    attributesAsDim,
    isPointData,
    latAttributeId,
    longAttributeId,
    srid,

    onLayoutShow,
    onTableLayoutSet,
    onTableFilterTreeSet,
    onMapLayoutSet,
    onChartLayoutSet,
    onLabelFormatSet,
    onVariationSet,
    onTableSettingsSet,
    onChartSettingsSet,
    onMapSettingsSet,
    onDetailLevelSet,
    onTimeSet,
    onDownloadSubmit,
    onTemporalDimOrderSet,
    chartLayoutCartesian,
    chartLayoutRadial,
    onChartSortingReset
  } = props;

  const {t} = useTranslation();

  const [fontSize, setFontSize] = useState(FONT_SIZE_SELECTOR_FONT_SIZE_MD);

  const [commonSettings, setCommonSettings] = useState(null);

  const [isTableSettingVisible, setTableSettingsVisibility] = useState(false);
  const [tableSettings, setTableSettings] = useState(null);
  const [tableOrdering, setTableOrdering] = useState(null);

  const [isChartSettingVisible, setChartSettingsVisibility] = useState(false);
  const [chartSettingsTabId, setChartSettingsTabId] = useState(null);
  const [tmpChartSettings, setTmpChartSettings] = useState(null);

  const [isCommonMapSettingVisible, setCommonMapSettingsVisibility] = useState(false);
  const [isMapSettingVisible, setMapSettingsVisibility] = useState(false);
  const [tmpMapSettings, setTmpMapSettings] = useState(null);
  const [isMapSettingsValid, setMapSettingsValidity] = useState(true);

  const [isTableLayoutLightVisible, setTableLayoutLightVisibility] = useState(false);

  const [hierarchyOnlyAttributes] = useState(nodeExtras?.HierarchyOnlyAttributes || []);
  const [hideHierarchyOnlyRows] = useState(nodeExtras?.HideHierarchyOnlyRows || false);

  const [isLayoutDialogVisible, setLayoutDialogVisible] = useState(false);
  const [tmpChartLayout, setTmpChartLayout] = useState(null);

  const chartRef = useRef();

  useEffect(() => {
    const func = () => handleStyle(mapId);
    window.addEventListener("resize", func);
    return () => window.removeEventListener("resize", func);
  }, [mapId]);

  useEffect(() => {
    handleStyle(mapId);
  });

  const decodeHeaderSideCellsMode = () => {
    if (!splitHeaderCells && splitSideCells) {
      return TABLE_SETTINGS_CELLS_HEADER_MERGE_SIDE_SPLIT;
    } else if (splitHeaderCells && !splitSideCells) {
      return TABLE_SETTINGS_CELLS_HEADER_SPLIT_SIDE_MERGE;
    } else if (splitHeaderCells && splitSideCells) {
      return TABLE_SETTINGS_CELLS_HEADER_SPLIT_SIDE_SPLIT;
    } else {
      return TABLE_SETTINGS_CELLS_HEADER_MERGE_SIDE_MERGE;
    }
  };

  const encodeTableSettingsToDispatch = () => {
    const ts = {
      ...commonSettings
    };
    switch (tableSettings.mergeSplitCells) {
      case TABLE_SETTINGS_CELLS_HEADER_MERGE_SIDE_MERGE:
        ts.splitHeaderCells = false;
        ts.splitSideCells = false;
        break;
      case TABLE_SETTINGS_CELLS_HEADER_MERGE_SIDE_SPLIT:
        ts.splitHeaderCells = false;
        ts.splitSideCells = true;

        break;
      case TABLE_SETTINGS_CELLS_HEADER_SPLIT_SIDE_MERGE:
        ts.splitHeaderCells = true;
        ts.splitSideCells = false;
        break;
      case TABLE_SETTINGS_CELLS_HEADER_SPLIT_SIDE_SPLIT:
        ts.splitHeaderCells = true;
        ts.splitSideCells = true;
        break;
      default:
        ts.splitHeaderCells = false;
        ts.splitSideCells = false;
        break;
    }

    return ts;
  };

  useEffect(() => {
    setTmpMapSettings(mapSettings);
  }, [mapSettings]);

  const handleTableOrdering = useCallback(() => {
    const isTimeDimInTable = !tableLayout?.filters.includes(timeDim);
    const order = temporalDimOrder === TEMPORAL_DIM_ORDER_SELECTOR_VALUE_DESC && isTimeDimInTable ? [timeDim] : null;
    setTableOrdering(order);
  }, [temporalDimOrder, tableLayout, timeDim, setTableOrdering]);

  useEffect(() => {
    handleTableOrdering();
  }, [temporalDimOrder, handleTableOrdering]);

  const onTablePageGenerationComplete = useCallback(
    generatedTableStructure => {
      const timings = generatedTableStructure.timings;
      if (timings) {
        onTimeSet(Math.round(generatedTableStructure.timings * 100) / 100);
      }
    },
    [onTimeSet]
  );

  const handleFilterSelect = useCallback(
    (dimension, value) => {
      if (viewerIdx === 0) {
        onTableLayoutSet(getUpdatedLayout(dimension, value, tableLayout, jsonStat, tableFilterTree));
      } else if (viewerIdx === 1) {
        onMapLayoutSet(getUpdatedLayout(dimension, value, mapLayout, jsonStat, mapFilterTree));
      } else if (viewerIdx >= 2) {
        const updatedLayout = getUpdatedLayout(dimension, value, chartLayout, jsonStat, chartFilterTree);
        const isChartCartesian = isChartLayoutCartesianByViewerIdx(viewerIdx);

        const obsSortingOrder = isChartCartesian
          ? chartSettings.obsSortingOrderCartesian
          : chartSettings.obsSortingOrderRadial;

        const obsSortingValue = isChartCartesian
          ? chartSettings.obsSortingValueCartesian
          : chartSettings.obsSortingValueRadial;

        const isChartSorted = obsSortingOrder !== CHART_DIM_ORDER_SELECTOR_VALUE_DEFAULT;
        const isSortingValueInFilters =
          obsSortingValue.length > 0 ? updatedLayout.secondaryDimValues.includes(obsSortingValue) : true;

        if (isChartSorted && !isSortingValueInFilters) {
          setLayoutDialogVisible(true);
          setTmpChartLayout(updatedLayout);
        } else {
          onChartLayoutSet(updatedLayout);
        }
      }
    },
    [
      jsonStat,
      viewerIdx,
      tableLayout,
      tableFilterTree,
      onTableLayoutSet,
      mapLayout,
      mapFilterTree,
      onMapLayoutSet,
      chartLayout,
      chartFilterTree,
      onChartLayoutSet,
      chartSettings
    ]
  );

  const handleChartLayoutSet = layout => {
    setLayoutDialogVisible(false);
    onChartSortingReset(isChartLayoutCartesianByViewerIdx(viewerIdx));
    onChartLayoutSet(layout);
  };

  const handleTableSettingsOpen = () => {
    setCommonSettings({temporalDimOrder, labelFormat});
    setTableSettings({mergeSplitCells: decodeHeaderSideCellsMode(), fontSize: fontSize});

    setTableSettingsVisibility(true);
  };

  const handleTableSettingsClose = () => {
    setTableSettings({...tableSettings, fontSize: fontSize});
    setCommonSettings({temporalDimOrder, labelFormat});
    setTableSettingsVisibility(false);
  };

  const handleTableSettingsSubmit = () => {
    setTableSettingsVisibility(false);
    onTableSettingsSet(encodeTableSettingsToDispatch());
    setFontSize(tableSettings.fontSize);
  };

  const handleChartSettingsOpen = () => {
    setTmpChartSettings(chartSettings);
    setCommonSettings({temporalDimOrder, labelFormat});
    setChartSettingsTabId("general");
    setChartSettingsVisibility(true);
  };

  const handleChartSettingsClose = () => {
    setChartSettingsVisibility(false);
    setTmpChartSettings(chartSettings);

    setCommonSettings({temporalDimOrder, labelFormat});
  };

  const handleChartSettingsSubmit = () => {
    setChartSettingsVisibility(false);
    onChartSettingsSet(tmpChartSettings);

    onTemporalDimOrderSet(commonSettings.temporalDimOrder);
    onLabelFormatSet(commonSettings.labelFormat);
  };

  const handleCommonMapSettingsClose = () => {
    setCommonSettings({labelFormat});
    setCommonMapSettingsVisibility(false);
  };

  const handleCommonMapSettingsOpen = () => {
    setCommonSettings({labelFormat});
    setCommonMapSettingsVisibility(true);
  };

  const handleCommonMapSettingsSubmit = () => {
    setCommonMapSettingsVisibility(false);
    onLabelFormatSet(commonSettings.labelFormat);
    onMapSettingsSet(tmpMapSettings);
  };

  const handleMapSettingsOpen = settings => {
    setTmpMapSettings(settings);
    setMapSettingsValidity(true);
    setMapSettingsVisibility(true);
  };

  const handleMapSettingsClose = () => {
    setMapSettingsVisibility(false);
  };

  const handleMapSettingsSubmit = () => {
    setMapSettingsVisibility(false);
    onMapSettingsSet(tmpMapSettings);
  };

  const chartZoomReset = useCallback(() => {
    if (chartRef && chartRef.current) chartRef.current.zoomReset();
  }, [chartRef]);

  const chartZoomIn = useCallback(() => {
    if (chartRef && chartRef.current) chartRef.current.zoomIn();
  }, [chartRef]);

  const chartZoomOut = useCallback(() => {
    if (chartRef && chartRef.current) chartRef.current.zoomOut();
  }, [chartRef]);

  if (!jsonStat) {
    let text = "";
    if (datasetFetchStart) {
      text = "";
    } else if (isTooBigData) {
      text = t("scenes.dataViewer.errors.tooBigData");
    } else if (isEmptyData) {
      text = t("scenes.dataViewer.errors.emptyData");
    } else if (datasetFetchError) {
      text = t("scenes.dataViewer.errors.fetchData");
    } else if (dimensions) {
      text = t("scenes.dataViewer.errors.applyCriteria");
    }
    return (
      <Card
        sx={{
          width: "100%",
          height: "100%",
          position: "relative"
        }}
      >
        <CustomEmpty text={text} />
      </Card>
    );
  }

  return (
    <Fragment>
      <Card
        sx={{
          width: "100%",
          height: "100%",
          padding: "16px"
        }}
      >
        {jsonStat && viewerIdx !== null && (
          <div id="data-viewer__viewer__header">
            {jsonStat && isPartialData && (
              <Alert
                severity="warning"
                sx={{
                  width: "100%",
                  marginBottom: "16px"
                }}
              >
                {t("scenes.dataViewer.warnings.maxObservations.label", {
                  maxObservations: maxObservations ? numberFormatter(maxObservations) : ""
                })}
              </Alert>
            )}
            <Box
              id="data-viewer__viewer__header__filters-actions-container"
              sx={{
                width: "100%",
                paddingBottom: theme => theme.spacing(1),
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start"
              }}
            >
              <Box id="data-viewer__viewer__header__filters">
                <DatasetFiltersJsonStatWrapper
                  jsonStat={jsonStat}
                  timeDim={timeDim}
                  layout={viewerIdx === 0 ? tableLayout : viewerIdx === 1 ? mapLayout : chartLayout}
                  filterTree={viewerIdx === 0 ? tableFilterTree : viewerIdx === 1 ? mapFilterTree : chartFilterTree}
                  labelFormat={labelFormat}
                  onSelect={handleFilterSelect}
                  enclosingContainerId={"#data-viewer__viewer__header__filters-actions-container"} // data-viewer__viewer__header
                />
              </Box>
              <Box id="data-viewer__viewer__header__actions" sx={{marginBottom: "8px"}}>
                <Grid container sx={{alignItems: "baseline"}}>
                  {enableVariation &&
                    (viewerIdx === 0 ||
                      getViewerTypeFromIdx(viewerIdx) === CHART_TYPE_BAR ||
                      getViewerTypeFromIdx(viewerIdx) === CHART_TYPE_LINE) && (
                      <Grid id="variation-selector" sx={actionStyle}>
                        <VariationSelector
                          showTrend={showTrend}
                          showCyclical={showCyclical}
                          onVariationSet={onVariationSet}
                        />
                      </Grid>
                    )}
                  {viewerIdx >= 2 && (
                    <Fragment>
                      <Grid id="dataset-zoom-reset-container">
                        <ChartZoomReset id={"dataset-zoom-reset-btn"} color={"action"} onClick={chartZoomReset} />
                      </Grid>
                      <Grid id="dataset-zoom-out-container">
                        <ChartZoomOut id={"dataset-zoom-out-btn"} color={"action"} onClick={chartZoomOut} />
                      </Grid>
                      <Grid id="dataset-zoom-in-container">
                        <ChartZoomIn id={"dataset-zoom-in-btn"} color={"action"} onClick={chartZoomIn} />
                      </Grid>
                    </Fragment>
                  )}
                  <Grid id="export-csv" sx={actionStyle}>
                    <Tooltip title={t("scenes.dataViewer.actions.exportCsv.tooltip")}>
                      <IconButton
                        aria-label={t("scenes.dataViewer.actions.exportCsv.ariaLabel")}
                        onClick={() => {
                          const exportParams = {
                            labelFormat: labelFormat,
                            customLabelFormat: customLabelFormats,
                            decimalSeparator: decimalSeparator,
                            roundingStrategy: roundingStrategy,
                            decimalNumber: decimalPlaces,
                            emptyCellPlaceHolder: tableEmptyChar,
                            hasVariation: enableVariation,
                            showTrend: showTrend,
                            showCyclical: showCyclical,
                            temporalDimOrder: temporalDimOrder
                          };
                          const layout = viewerIdx === 0 ? tableLayout : viewerIdx === 1 ? mapLayout : chartLayout;

                          const newCriteria = getFilteredCriteriaFromLayout(criteria, dimensions, layout, timeDim);
                          if (viewerIdx === 1 && layout?.territoryDim) {
                            const territoryDim = layout.territoryDim;
                            newCriteria[territoryDim] = {
                              id: territoryDim,
                              filterValues: window.LMap.getDataIds(mapId)
                            };
                          }

                          onDownloadSubmit(
                            nodeId,
                            packageId,
                            datasetId,
                            datasetTitle,
                            newCriteria,
                            lastNObservations,
                            layout,
                            DOWNLOAD_FORMAT_CSV,
                            downloadFormats()[DOWNLOAD_FORMAT_CSV].extension,
                            false,
                            exportParams,
                            defaultLanguage,
                            languages,
                            t
                          );
                        }}
                      >
                        <CsvIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  {viewerIdx >= 2 && (
                    <Grid id="chart-settings" sx={actionStyle}>
                      <Tooltip title={t("scenes.dataViewer.actions.chartSettings.tooltip")}>
                        <IconButton
                          aria-label={t("scenes.dataViewer.actions.chartSettings.ariaLabel")}
                          onClick={handleChartSettingsOpen}
                        >
                          <SettingsIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                  {viewerIdx === 1 && (
                    <Grid id="map-settings" sx={actionStyle}>
                      <Tooltip title={t("scenes.dataViewer.actions.mapSettings.tooltip")}>
                        <IconButton
                          aria-label={t("scenes.dataViewer.actions.mapSettings.ariaLabel")}
                          onClick={handleCommonMapSettingsOpen}
                        >
                          <SettingsIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                  {viewerIdx === 0 && (
                    <Grid id="table-settings" sx={actionStyle}>
                      <Tooltip title={t("scenes.dataViewer.actions.tableSettings.tooltip")}>
                        <IconButton
                          aria-label={t("scenes.dataViewer.actions.tableSettings.ariaLabel")}
                          onClick={handleTableSettingsOpen}
                          id="data_viewer_single_viewer_viewer_settings_btn"
                        >
                          <SettingsIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </div>
        )}
        <Box
          id="data-viewer__viewer__viewers"
          sx={{
            width: "100%",
            height: "100%",
            position: "relative"
          }}
        >
          {(() => {
            if (viewerIdx === 0 && tableLayout) {
              return getDatasetSize(dimensionsInfo, tableLayout) > maxCells ? (
                <Call cb={setTableLayoutLightVisibility} cbParam={true}>
                  <CustomEmpty text={t("scenes.dataViewer.singleViewer.errors.tooBigTable")} />
                </Call>
              ) : (
                <Table
                  jsonStat={jsonStat}
                  layout={tableLayout}
                  isFullscreen={isFullscreen}
                  labelFormat={labelFormat}
                  showTrend={showTrend}
                  showCyclical={showCyclical}
                  fontSize={
                    fontSize === FONT_SIZE_SELECTOR_FONT_SIZE_SM
                      ? JSONSTAT_TABLE_FONT_SIZE_SM
                      : fontSize === FONT_SIZE_SELECTOR_FONT_SIZE_MD
                        ? JSONSTAT_TABLE_FONT_SIZE_MD
                        : JSONSTAT_TABLE_FONT_SIZE_LG
                  }
                  decimalSeparator={decimalSeparator}
                  roundingStrategy={roundingStrategy}
                  decimalPlaces={decimalPlaces}
                  emptyChar={tableEmptyChar}
                  invertedDims={tableOrdering}
                  hierarchyOnlyAttributes={hierarchyOnlyAttributes}
                  hideHierarchyOnlyRows={hideHierarchyOnlyRows}
                  onPageGenerationComplete={onTablePageGenerationComplete}
                  filterable
                  sortable
                  splitHeaderCells={splitHeaderCells}
                  splitSideCells={splitSideCells}
                  scrollToLastRow
                  attributesAsDim={attributesAsDim}
                />
              );
            } else if (viewerIdx === 1 && mapLayout) {
              return (
                <Map
                  mapId={mapId}
                  nodeId={nodeId}
                  jsonStat={jsonStat}
                  layout={mapLayout}
                  labelFormat={labelFormat}
                  decimalSeparator={decimalSeparator}
                  roundingStrategy={roundingStrategy}
                  decimalPlaces={decimalPlaces}
                  defaultDetailLevel={detailLevel}
                  onDetailLevelChange={onDetailLevelSet}
                  initialBaseLayer={mapSettings.baseLayer}
                  defaultSettings={{
                    isLegendCollapsed: mapSettings.isLegendCollapsed,
                    opacity: mapSettings.opacity
                  }}
                  settings={{
                    classificationMethod: mapSettings.classificationMethod,
                    paletteStartColor: mapSettings.paletteStartColor,
                    paletteEndColor: mapSettings.paletteEndColor,
                    paletteCardinality: mapSettings.paletteCardinality,
                    customIntervals: mapSettings.customIntervals
                  }}
                  setSettings={onMapSettingsSet}
                  isFullscreen={isFullscreen}
                  showSelection
                  disableSettings
                  onSettingsShow={handleMapSettingsOpen}
                  showSingleGeometry={showSingleGeometry}
                  isPointData={isPointData}
                  latAttributeId={latAttributeId}
                  longAttributeId={longAttributeId}
                  srid={srid}
                />
              );
            } else if (viewerIdx >= 2 && chartLayout) {
              const isTimeDimInChart = !chartLayout.filters.includes(timeDim);
              return (
                <ChartJsonStatWrapper
                  key={viewerIdx} // added to force chart render on type change, due the layout change
                  ref={chartRef}
                  chartId={chartId}
                  type={viewersFactory(t)[viewerIdx].chartType}
                  jsonStat={jsonStat}
                  layout={chartLayout}
                  timePeriodsByFreq={timePeriodsByFreq}
                  labelFormat={labelFormat}
                  showTrend={showTrend}
                  showCyclical={showCyclical}
                  decimalSeparator={decimalSeparator}
                  roundingStrategy={roundingStrategy}
                  decimalPlaces={decimalPlaces}
                  chartSettings={chartSettings}
                  invertedDims={
                    temporalDimOrder === TEMPORAL_DIM_ORDER_SELECTOR_VALUE_DESC && isTimeDimInChart ? [timeDim] : null
                  }
                />
              );
            } else {
              return <CustomEmpty text={t("scenes.dataViewer.errors.viewerNotAvailable")} />;
            }
          })()}
        </Box>
      </Card>

      <CustomDialog
        id="data-viever__table-settings-dialog"
        open={isTableSettingVisible}
        onClose={handleTableSettingsClose}
      >
        <CustomDialogTitle id={"components-fullscreen-dialog-label-id"} onClose={handleTableSettingsClose}>
          {t("scenes.dataViewer.singleViewer.dialogs.table.title")}
        </CustomDialogTitle>
        <DialogContent>
          <TableSettings
            commonSettings={commonSettings}
            setCommonSettings={setCommonSettings}
            tableSettings={tableSettings}
            setTableSettings={setTableSettings}
          />
        </DialogContent>
        <DialogActions>
          <Button id="data-viever__table-settings-dialog__cancel-btn" onClick={handleTableSettingsClose}>
            {t("commons.confirm.cancel")}
          </Button>
          <Button
            id="data-viever__table-settings-dialog__apply-btn"
            color="primary"
            onClick={handleTableSettingsSubmit}
          >
            {t("commons.confirm.apply")}
          </Button>
        </DialogActions>
      </CustomDialog>

      <CustomDialog
        id="data-viever__chart-settings-dialog"
        open={isChartSettingVisible}
        onClose={handleChartSettingsClose}
      >
        <CustomDialogTitle onClose={handleChartSettingsClose}>
          {t("scenes.dataViewer.singleViewer.dialogs.chartSettings.title")}
        </CustomDialogTitle>
        <DialogContent sx={{height: "480px"}}>
          <Box sx={{height: "48px"}}>
            <Tabs
              value={chartSettingsTabId}
              variant="scrollable"
              scrollButtons="auto"
              onChange={(event, newValue) => setChartSettingsTabId(newValue)}
            >
              <Tab
                id="data-viever__chart-settings-dialog__general"
                value={"general"}
                label={t("scenes.dataViewer.singleViewer.dialogs.chartSettings.tabs.general")}
              />
              <Tab
                id="data-viever__chart-settings-dialog__colors"
                value={"colors"}
                label={t("scenes.dataViewer.singleViewer.dialogs.chartSettings.tabs.colors")}
              />
            </Tabs>
          </Box>
          <Box
            sx={{
              height: "calc(100% - 64px)",
              marginTop: "16px",
              paddingRight: "4px",
              overflowY: "auto",
              overflowX: "hidden"
            }}
          >
            {chartSettingsTabId === "general" && (
              <ChartSettingsGeneral
                commonSettings={commonSettings}
                setCommonSettings={setCommonSettings}
                settings={tmpChartSettings}
                onSettingsSet={setTmpChartSettings}
                cartesianSecondaryDim={chartLayoutCartesian.secondaryDim.toString()}
                cartesianSecondaryDimValues={chartLayoutCartesian.secondaryDimValues}
                radialSecondaryDim={chartLayoutRadial.secondaryDim.toString()}
                radialSecondaryDimValues={chartLayoutRadial.secondaryDimValues}
                getDimLabel={dimensionId => getDimensionLabel(jsonStat, null, dimensionId, t)}
                getDimValueLabel={(dimensionId, valueId) =>
                  getFormattedDimensionValueLabel(jsonStat, null, dimensionId, valueId, labelFormat, t)
                }
              />
            )}
            {chartSettingsTabId === "colors" && (
              <ChartSettingsColors
                settings={tmpChartSettings}
                dimensions={jsonStat?.id || []}
                onSettingsSet={setTmpChartSettings}
                dataHasDimension={dim => jsonStat.id.includes(dim)}
                dataHasDimensionValue={(dim, dimVal) => jsonStat.dimension[dim].category.index.includes(dimVal)}
                getDimLabel={dimensionId => getDimensionLabel(jsonStat, null, dimensionId, t)}
                getDimValueLabel={(dimensionId, valueId) =>
                  getDimensionValueLabel(jsonStat, null, dimensionId, valueId, t)
                }
                getDimensionValues={dim => jsonStat.dimension[dim].category.index || []}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button id="data-viever__chart-settings-dialog__cancel-btn" onClick={handleChartSettingsClose}>
            {t("commons.confirm.cancel")}
          </Button>
          <Button
            id="data-viever__chart-settings-dialog__apply-btn"
            autoFocus
            onClick={handleChartSettingsSubmit}
            color="primary"
          >
            {t("commons.confirm.apply")}
          </Button>
        </DialogActions>
      </CustomDialog>

      <CustomDialog
        id="data-viever__common-map-settings-dialog"
        open={isCommonMapSettingVisible}
        onClose={handleCommonMapSettingsClose}
      >
        <CustomDialogTitle id="data-viever__common-map-settings-dialog__title" onClose={handleCommonMapSettingsClose}>
          {t("scenes.dataViewer.singleViewer.dialogs.mapSettings.title")}
        </CustomDialogTitle>
        <DialogContent>
          <Box sx={{marginBottom: "24px"}}>
            <MapGeneralSettings commonSettings={commonSettings} setCommonSettings={setCommonSettings} />
          </Box>
          <Paper variant="outlined" sx={paperStyle}>
            <CardHeader
              subheader={t("scenes.dataViewer.singleViewer.dialogs.mapSettings.title")}
              sx={cardHeaderStyle}
            />
            <MapSettingsSettings
              mapId={mapId}
              settings={tmpMapSettings}
              onSettingsSet={setTmpMapSettings}
              setSettingsValidity={setMapSettingsValidity}
            />
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button id="data-viever__common-map-settings-dialog__cancel-btn" onClick={handleCommonMapSettingsClose}>
            {t("commons.confirm.cancel")}
          </Button>
          <Button
            id="data-viever__common-map-settings-dialog__apply-btn"
            color="primary"
            onClick={handleCommonMapSettingsSubmit}
            disabled={!isMapSettingsValid}
          >
            {t("commons.confirm.apply")}
          </Button>
        </DialogActions>
      </CustomDialog>

      <CustomDialog id="data-viever__map-settings-dialog" open={isMapSettingVisible} onClose={handleMapSettingsClose}>
        <CustomDialogTitle onClose={handleMapSettingsClose}>
          {t("scenes.dataViewer.singleViewer.dialogs.mapSettings.title")}
        </CustomDialogTitle>
        <DialogContent>
          <MapSettingsSettings
            mapId={mapId}
            settings={tmpMapSettings}
            onSettingsSet={setTmpMapSettings}
            setSettingsValidity={setMapSettingsValidity}
          />
        </DialogContent>
        <DialogActions>
          <Button id="data-viever__map-settings-dialog__cancel-btn" onClick={handleMapSettingsClose}>
            {t("commons.confirm.cancel")}
          </Button>
          <Button
            id="data-viever__map-settings-dialog__apply-btn"
            color="primary"
            onClick={handleMapSettingsSubmit}
            disabled={!isMapSettingsValid}
          >
            {t("commons.confirm.apply")}
          </Button>
        </DialogActions>
      </CustomDialog>

      <AlertDialog
        id="viewer-chart-layout__dialog"
        open={isLayoutDialogVisible}
        onClose={() => setLayoutDialogVisible(false)}
      >
        <CustomDialogTitle onClose={() => setLayoutDialogVisible(false)}>
          {t("scenes.dataViewer.singleViewer.dialogs.layoutDialog.title")}
        </CustomDialogTitle>
        <DialogContent>{t("scenes.dataViewer.singleViewer.dialogs.layoutDialog.content")}</DialogContent>
        <DialogActions>
          <Button onClick={() => setLayoutDialogVisible(false)}>{t("commons.confirm.cancel")}</Button>
          <Button onClick={() => handleChartLayoutSet(tmpChartLayout)}>{t("commons.confirm.confirm")}</Button>
        </DialogActions>
      </AlertDialog>

      <TableLayoutLight
        open={isTableLayoutLightVisible}
        onClose={() => setTableLayoutLightVisibility(false)}
        onSubmit={newLayout => {
          const newTableFilterTree = getFilterTreeFromJsonStat(newLayout.filters, jsonStat); // TODO: use worker
          const newFiltersValue = getInitialFiltersValue(jsonStat, newLayout, newTableFilterTree);

          onTableFilterTreeSet(newTableFilterTree);
          onTableLayoutSet({
            ...newLayout,
            filtersValue: newFiltersValue
          });
        }}
        onLayoutOpen={onLayoutShow}
        layout={tableLayout}
        dimensionsInfo={dimensionsInfo}
        limit={maxCells}
      />
    </Fragment>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(SingleViewerViewer);
